"use client";

import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  HouseIcon,
  HeartIcon,
  ChatCircleIcon,
  UserIcon,
  MapPinIcon,
  SpinnerGapIcon,
  NavigationArrowIcon,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslations, useLocale } from "next-intl";
import { getCurrentLocation, toBounds } from "@/lib/geolocation";
import { nearbyScopeHref } from "@/lib/search/search-url";
import { useUnreadChatCount } from "@/lib/realtime/unread-chat-store";
import { useAppBadge } from "@/hooks/useAppBadge";

type NavItem = {
  id: string;
  label: string;
  icon: PhosphorIcon;
  href: string | null;
  isActive: boolean;
  /** 로그인 상태 확인 전 자리표시 — 같은 크기의 회색 블록만 그린다. */
  placeholder?: boolean;
};

export default function BottomNavBar() {
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("common.nav");
  const tSearch = useTranslations("common.search");
  // 로그인 상태의 단일 출처는 AuthProvider. 확인 전(loading)에는 비로그인 레이아웃을
  // 그리지 않는다 — 로그인 사용자에게 "로그인" 탭이 잠깐 보였다 바뀌는 걸 막는다.
  const { status, user } = useAuth();
  const authLoading = status === "loading";
  const [isLoadingNearby, setIsLoadingNearby] = useState(false);
  const { setAppBadge } = useAppBadge();

  // Remove locale prefix for path checking
  const pathWithoutLocale = pathname.replace(`/${locale}`, "") || "/";

  // Only show on main, search, profile pages (not account-settings), messages list,
  // help center, and informational utility pages (unsubscribed, events, promotions)
  // — users may want to navigate elsewhere after taking the action.
  const shouldShow =
    pathWithoutLocale === "/" ||
    pathWithoutLocale.startsWith("/s/") ||
    pathWithoutLocale.startsWith("/partner/") ||
    pathWithoutLocale.startsWith("/stations/") ||
    pathWithoutLocale.startsWith("/models/") ||
    (pathWithoutLocale.startsWith("/profile") &&
      !pathWithoutLocale.startsWith("/account-settings")) ||
    pathWithoutLocale === "/messages" ||
    pathWithoutLocale.startsWith("/help") ||
    pathWithoutLocale === "/unsubscribed" ||
    pathWithoutLocale.startsWith("/events") ||
    pathWithoutLocale.startsWith("/promotions");

  // 전역 unread 카운트는 공유 스토어 1곳에서만 realtime 구독한다 — UserMenu와
  // 각자 채널을 만들면 유저당 구독이 2배가 되고, realtime은 chat_rooms write마다
  // 모든 구독에 RLS를 평가해 동시접속 급증 시 DB CPU를 태운다 (2026-06-12
  // 콘서트 장애). 초기 조회·debounce·visibility/online 재연결도 스토어가 처리.
  const unreadCount = useUnreadChatCount(user?.id);

  useEffect(() => {
    setAppBadge(unreadCount);
  }, [unreadCount, setAppBadge]);

  // Handle navigation item click - refresh if already on the page
  const handleNavClick = (href: string, isActive: boolean) => {
    if (isActive) {
      // Already on this page - refresh
      router.refresh();
    } else {
      router.push(href);
    }
  };

  // Handle nearby search - GPS first, then IP fallback
  const handleNearbySearch = async () => {
    setIsLoadingNearby(true);
    try {
      const coords = await getCurrentLocation();
      if (coords) {
        router.push(
          nearbyScopeHref(locale, { bounds: toBounds(coords), timestamp: true }),
        );
      } else {
        alert(tSearch("locationError"));
      }
    } catch {
      alert(tSearch("locationError"));
    } finally {
      setIsLoadingNearby(false);
    }
  };

  // Don't render on product/checkout pages
  if (!shouldShow) {
    return null;
  }

  // Define navigation items
  const homeItem: NavItem = {
    id: "home",
    label: t("home"),
    icon: HouseIcon,
    href: `/${locale}`,
    isActive: pathWithoutLocale === "/",
  };
  // Nearby button - always shown (center position)
  const nearbyItem: NavItem = {
    id: "nearby",
    label: t("nearby"),
    icon: NavigationArrowIcon,
    href: null, // No href - uses onClick instead
    isActive: pathWithoutLocale.startsWith("/s/"),
  };
  const placeholderItem = (id: string): NavItem => ({
    id,
    label: "",
    icon: HouseIcon,
    href: null,
    isActive: false,
    placeholder: true,
  });

  const navItems: NavItem[] = authLoading
    ? [
        homeItem,
        placeholderItem("wishlist"),
        nearbyItem,
        placeholderItem("messages"),
        placeholderItem("profile"),
      ]
    : [
        homeItem,
        // Wishlist - only for logged-in users
        ...(user
          ? [
              {
                id: "wishlist",
                label: t("wishlist"),
                icon: HeartIcon,
                href: `/${locale}/profile/wishlist`,
                isActive: pathWithoutLocale === "/profile/wishlist",
              },
            ]
          : []),
        nearbyItem,
        // Messages - only for logged-in users
        ...(user
          ? [
              {
                id: "messages",
                label: t("messages"),
                icon: ChatCircleIcon,
                href: `/${locale}/messages`,
                isActive:
                  pathWithoutLocale === "/messages" ||
                  pathWithoutLocale.startsWith("/messages/"),
              },
            ]
          : []),
        {
          id: "profile",
          label: user ? t("profile") : t("login"),
          icon: UserIcon,
          href: user ? `/${locale}/profile` : `/${locale}/login`,
          isActive:
            (pathWithoutLocale.startsWith("/profile") &&
              pathWithoutLocale !== "/profile/wishlist") ||
            pathWithoutLocale === "/login",
        },
      ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-[0_-1px_16px_rgba(0,0,0,0.06)] md:hidden pb-safe">
      <div className="flex items-center justify-center h-14">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isNearby = item.id === "nearby";

          // 로그인 확인 전 자리표시: 실제 탭과 같은 크기(아이콘 24px + 라벨 줄)의 회색 블록.
          if (item.placeholder) {
            return (
              <div
                key={item.id}
                aria-hidden
                className="flex-1 flex items-center justify-center max-w-[20%] min-h-14"
              >
                <div className="flex flex-col items-center justify-center gap-1 py-2">
                  <div className="size-6 rounded-full bg-gray-100" />
                  <div className="h-[15px] w-7 rounded bg-gray-100" />
                </div>
              </div>
            );
          }

          const isMessages = item.id === "messages";
          const showBadge = isMessages && unreadCount > 0;

          // Content for nav item — toss-style: unified dark gray, active = fill only
          const content = (
            <div className="flex flex-col items-center justify-center gap-1 py-2 text-gray-700 transition-transform duration-150 active:scale-95">
              <div className="relative">
                {isNearby && isLoadingNearby ? (
                  <SpinnerGapIcon
                    size={24}
                    weight="bold"
                    className="animate-spin"
                  />
                ) : (
                  <Icon size={24} weight={item.isActive ? "fill" : "regular"} />
                )}
                {showBadge && (
                  <span className="absolute -top-1.5 -right-2 min-w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              <span
                className={cn(
                  "text-[10px] transition-all",
                  item.isActive ? "font-semibold" : "font-normal",
                )}
              >
                {item.label}
              </span>
            </div>
          );

          // "nearby" button uses onClick instead of href
          if (isNearby) {
            return (
              <button
                key={item.id}
                onClick={handleNearbySearch}
                disabled={isLoadingNearby}
                className="flex-1 flex items-center justify-center max-w-[20%] min-h-14"
                aria-label={item.label}
              >
                {content}
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.href!, item.isActive)}
              className="flex-1 flex items-center justify-center max-w-[20%] min-h-14"
              aria-label={item.label}
            >
              {content}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
